const XLSX = require('@sheet/core')
const { OUTBOUND_TEMPLATE_COLUMNS, OUTBOUND_SHIPMENT_TEMPLATE_HEADERS } = require('./constants')

const numRows = 10 // PREFILLING THE FIRST TEN ROWS OF THE EXCEL SHEET

// We intentionally allow the appearance of Destination Name & Product Name
// for visual guidance — they are stripped out during upload by convertExcelOutboundShipmentToCsv
const COLUMN_CONFIG = {
  [OUTBOUND_TEMPLATE_COLUMNS.DESTINATION_NAME]: { width: 30, index: 0 },
  [OUTBOUND_TEMPLATE_COLUMNS.DESTINATION_WAREHOUSE_CODE]: { width: 30, index: 1 },
  [OUTBOUND_TEMPLATE_COLUMNS.PRODUCT_NAME]: { width: 30, index: 2 },
  [OUTBOUND_TEMPLATE_COLUMNS.PRODUCT_CODE]: { width: 25, index: 3 },
  [OUTBOUND_TEMPLATE_COLUMNS.QUANTITY]: { width: 15, index: 4 },
  [OUTBOUND_TEMPLATE_COLUMNS.ORIGIN_WAREHOUSE_CODE]: { width: 25, index: 5 },
  [OUTBOUND_TEMPLATE_COLUMNS.EXTERNAL_ID]: { width: 20, index: 6 },
  [OUTBOUND_TEMPLATE_COLUMNS.DATE]: { width: 20, index: 7 },
  [OUTBOUND_TEMPLATE_COLUMNS.BATCH_NO]: { width: 20, index: 8 },
  [OUTBOUND_TEMPLATE_COLUMNS.MANUFACTURER]: { width: 25, index: 9 },
  [OUTBOUND_TEMPLATE_COLUMNS.EXPIRY]: { width: 20, index: 10 }
}

const getOutboundExcelTemplate = async (state, { warehouseCode, facilities, products }) => {
  const workbook = XLSX.utils.book_new()

  //  Warehouses manage their product inventory in pack sizes and send outbound shipments in packs
  const packSizeProducts = products.filter(p => p.genericParent && p.code !== p.genericParent)

  // --- Facilities sheet ---
  const facilitiesData = [
    ['Facility Name', 'Warehouse Code'],
    ...facilities.map(f => [f.name, f.additionalData.warehouseCode || ''])
  ]

  const facilitiesSheet = XLSX.utils.aoa_to_sheet(facilitiesData)
  facilitiesSheet['!cols'] = [{ wch: 45 }, { wch: 25 }]
  XLSX.utils.book_append_sheet(workbook, facilitiesSheet, 'Facilities')

  // --- Products sheet ---
  const productsData = [
    ['Product Name', 'Product Code'],
    ...packSizeProducts.map(p => [p.name, p.code])
  ]
  const productsSheet = XLSX.utils.aoa_to_sheet(productsData)
  productsSheet['!cols'] = [{ wch: 40 }, { wch: 25 }]
  XLSX.utils.book_append_sheet(workbook, productsSheet, 'Products')

  // --- Outbound sheet ---
  const outboundHeaders = OUTBOUND_SHIPMENT_TEMPLATE_HEADERS.EXCEL_TEMPLATE
  const emptyRowCount = numRows - 1 // Subtract 1 for header row

  // Create empty rows programmatically based on header count
  const emptyRow = new Array(outboundHeaders.length).fill('')
  const outboundData = [
    outboundHeaders,
    ...Array(emptyRowCount).fill(null).map(() => [...emptyRow])
  ]

  const outboundSheet = XLSX.utils.aoa_to_sheet(outboundData)

  // Set column widths from configuration
  outboundSheet['!cols'] = outboundHeaders.map(header => ({
    wch: COLUMN_CONFIG[header].width
  }))

  // Pre-fill origin warehouse code and format date columns
  // Starting at row 2 because:
  // - Excel is 1-indexed (row 1 = headers)
  // - We want to fill data rows starting after the header
  for (let rowIndex = 2; rowIndex <= numRows; rowIndex++) {
    // Pre-fill Origin Warehouse Code (column F, index 5)
    const originCodeCell = `F${rowIndex}`
    // In SheetJS, a cell is represented as:
    //   {
    //   t: 's',   // type
    //   v: value, // actual value
    //   c: []     // comments
    // }
    outboundSheet[originCodeCell] = {
      t: 's', // t = type: 's' for string, 'n' for number, 'b' for boolean
      v: warehouseCode // v = value: the actual cell value
    }

    // Format Date column (H, index 7) as date
    const dateCell = `H${rowIndex}`
    outboundSheet[dateCell] = {
      t: 'n', // n = number type (dates are numbers in Excel)
      z: 'dd/mm/yyyy' // z = number format string
    }

    // Format Expiry column (K, index 10) as date
    const expiryCell = `K${rowIndex}`
    outboundSheet[expiryCell] = {
      t: 'n',
      z: 'dd/mm/yyyy'
    }
  }

  // Define validation ranges using Excel reference syntax
  // Format: SheetName!$Column$StartRow:$Column$EndRow
  // Example: "Facilities!$A$2:$A$51" means:
  //   - Sheet: "Facilities"
  //   - Column: A (fixed with $)
  //   - Rows: 2 to 51 (fixed with $)
  //   - The $ makes the reference absolute (won't change when copied)
  const facilityRange = `Facilities!$A$2:$A$${facilities.length + 1}`
  const productRange = `Products!$A$2:$A$${packSizeProducts.length + 1}`

  const validations = []
  for (let rowIndex = 2; rowIndex <= numRows; rowIndex++) {
    // Destination Name dropdown (column A)
    validations.push({
      ref: `A${rowIndex}`,
      t: 'List',
      f: facilityRange
    })

    // Product Name dropdown (column C)
    validations.push({
      ref: `C${rowIndex}`,
      t: 'List',
      f: productRange
    })

    // Auto-fill Destination Warehouse Code using VLOOKUP
    const destinationCodeCell = `B${rowIndex}`
    outboundSheet[destinationCodeCell] = {
      t: 's',
      f: `IFERROR(VLOOKUP(A${rowIndex},Facilities!A:B,2,FALSE),"")`,
      v: ''
    }

    // Auto-fill Product Code using VLOOKUP
    const productCodeCell = `D${rowIndex}`
    outboundSheet[productCodeCell] = {
      t: 's',
      f: `IFERROR(VLOOKUP(C${rowIndex},Products!A:B,2,FALSE),"")`,
      v: ''
    }
  }

  outboundSheet['!validations'] = validations
  XLSX.utils.book_append_sheet(workbook, outboundSheet, 'Outbound')

  // Hide helper sheets (visibility: 0=visible, 1=hidden, 2=very hidden)
  XLSX.utils.book_set_sheet_visibility(workbook, 'Facilities', 2)
  XLSX.utils.book_set_sheet_visibility(workbook, 'Products', 2)

  const arrayBuffer = XLSX.write(workbook, {
    type: 'array',
    bookType: 'xlsx'
  })

  return new global.Blob([arrayBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
}

module.exports = {
  getOutboundExcelTemplate
}
